import { Component, HostBinding, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { SidenavService } from './sidenav.service';
import { AuthenticationService } from '../../services/base/authentication.service';
import { DOCUMENT } from '@angular/common';
import { Organisation } from '../../interfaces/routering/organisation';
import { FormControl } from '@angular/forms';
import { SearchAddressResults } from '../../interfaces/base/search-address-results';
import { debounceTime, distinctUntilChanged, timeout } from 'rxjs/operators';
import { NationaalGeoregisterSuggest } from '../../interfaces/base/nationaal-georegister-suggest';
import { Observable, Subscriber, TimeoutError } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { NationaalGeoregisterLocatieServerDocs, NationaalGeoregisterLookup } from '../../interfaces/base/nationaal-georegister-lookup';

@Component({
  selector: 'fury-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss']
})
export class SidenavComponent implements OnInit, OnDestroy {

  @Input()
  @HostBinding('class.collapsed')
  collapsed: boolean;

  public address: FormControl<string> = new FormControl(<string>null);
  public addressSuggestions: SearchAddressResults[] = [];
  public addressSuggestionsLoading: boolean = false;
  public addressSuggestionsNoResults: boolean = false;

  constructor(@Inject(DOCUMENT) private document: Document,
              private http: HttpClient,
              public authenticationService: AuthenticationService,
              public sidenavService: SidenavService,
  ) {
  }

  ngOnInit(): void {
    this.authenticationService.organisation$.subscribe((organisation: Organisation): void => {
      const classList: DOMTokenList = this.document.body.classList,
        logo: HTMLImageElement = document.getElementById('logo') as HTMLImageElement;

      if (logo !== null) {
        if (classList.contains('fury-default')) {
          logo.src = (organisation !== null && typeof organisation.logo !== 'undefined' && organisation.logo !== ''
              ? organisation.logo
              : 'assets/img/logo.svg'
          );
        } else {
          logo.src = 'assets/img/logo-darkmode.png';
        }
      }
    });

    this.address.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe((value: string | SearchAddressResults): void => {
      if (value) {
        if (typeof value === 'string') {
          this.getAddress(value).subscribe((response: NationaalGeoregisterSuggest): void => {
            if (response) {
              if (response.response.numFound > 0) {
                this.addressSuggestions = response.response.docs;
              } else {
                this.addressSuggestionsNoResults = true;
              }
            }

            this.addressSuggestionsLoading = false;
          });
        } else {
          this.selectAddress(value.id);
        }
      }
    });
  }

  getAddress(value: string): Observable<NationaalGeoregisterSuggest> {
    return new Observable<NationaalGeoregisterSuggest>((o: Subscriber<NationaalGeoregisterSuggest>): void => {
      this.authenticationService.organisation$.subscribe((organisation: Organisation): void => {
        const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
          '?q=' + encodeURIComponent(value) +
          '&rows=10' +
          '&bq=' + encodeURIComponent('type:provincie^0.5') +
          '&bq=' + encodeURIComponent('type:gemeente^0.5') +
          '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
          '&bq=' + encodeURIComponent('type:weg^0.5') +
          '&bq=' + encodeURIComponent('type:adres^1.5') +
          '&fq=bron:"BAG"' +
          (organisation ?
            '&fq=gemeentenaam:(' + organisation.center_municipality + ')'
            : '');

        this.http.get(url, {
          observe: 'response',
          responseType: 'json'
        }).pipe(
          timeout(5000)
        ).subscribe(
          (response: HttpResponse<NationaalGeoregisterSuggest>): void => {
            o.next(response.body);
          },
          (error): void => {
            if (error instanceof TimeoutError) {
              this.addressSuggestionsNoResults = true;
              this.addressSuggestionsLoading = false;
            }
            o.next(undefined);
          });
      });
    });
  }

  selectAddress(id: string): void {
    this.addressSuggestions = [];
    this.addressSuggestionsLoading = true;

    const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
      '?id=' + encodeURIComponent(id);

    this.http.get(url, {
      observe: 'response',
      responseType: 'json'
    }).pipe(
      timeout(5000)
    ).subscribe(
      (response: HttpResponse<NationaalGeoregisterLookup>): void => {
        if (response.body.response.numFound > 0) {
          const data: NationaalGeoregisterLocatieServerDocs = response.body.response.docs[0],
            dataLatLng: string[] = data.centroide_ll.replace('POINT(', '').replace(')', '').split(' ');

          this.sidenavService.updateMap$.next({lat: parseFloat(dataLatLng[1]), lng: parseFloat(dataLatLng[0])});
        }

        this.addressSuggestionsLoading = false;
      }
    );
  }

  formatAddress(value: NationaalGeoregisterSuggest): string {
    return value !== null && typeof value === 'object' ? value.weergavenaam : null;
  }

  clear(): void {
    this.address.setValue(null);
    this.sidenavService.updateMap$.next(null);
  }

  ngOnDestroy(): void {
  }
}
